import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Globe, Save, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CMSSiteIdentitySection({
  siteIdentity,
  setSiteIdentity,
  isSaving,
  onSave
}) {
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/cms/upload-site-asset?asset_type=logo`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      setSiteIdentity(prev => ({ ...prev, site_logo: res.data.url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/cms/upload-site-asset?asset_type=favicon`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      setSiteIdentity(prev => ({ ...prev, favicon: res.data.url }));
      toast.success('Favicon uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/cms/upload-site-asset?asset_type=og_image`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      setSiteIdentity(prev => ({ ...prev, og_image: res.data.url }));
      toast.success('Social sharing image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="site-identity-section">
      <div className="flex items-center gap-3 mb-4">
        <Globe className="w-5 h-5 text-amber-600" />
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">Site Identity</h3>
          <p className="text-sm text-slate-500">Controls the browser tab title and tagline</p>
        </div>
      </div>

      {/* Title and Tagline */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Site Title</Label>
          <Input
            value={siteIdentity.site_title}
            onChange={(e) => setSiteIdentity(prev => ({ ...prev, site_title: e.target.value }))}
            className="h-12 mt-1.5"
            placeholder="HandyBayani"
            data-testid="site-title-input"
          />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input
            value={siteIdentity.site_tagline}
            onChange={(e) => setSiteIdentity(prev => ({ ...prev, site_tagline: e.target.value }))}
            className="h-12 mt-1.5"
            placeholder="Home Services Marketplace"
            data-testid="site-tagline-input"
          />
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Browser tab will show: &quot;{siteIdentity.site_title} - {siteIdentity.site_tagline}&quot;
      </p>

      {/* Logo & Favicon Upload */}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <Label>Site Logo</Label>
          <div className="mt-1.5 flex items-center gap-3">
            <div className="relative w-16 h-16 border rounded-lg overflow-hidden bg-stone-50 flex items-center justify-center">
              {siteIdentity.site_logo ? (
                <>
                  <img 
                    src={siteIdentity.site_logo.startsWith('http') ? siteIdentity.site_logo : `${BACKEND_URL}${siteIdentity.site_logo}`} 
                    alt="Logo" 
                    className="w-full h-full object-contain" 
                  />
                  <button 
                    onClick={() => setSiteIdentity(prev => ({ ...prev, site_logo: '' }))} 
                    className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white rounded-bl text-xs flex items-center justify-center"
                  >
                    &times;
                  </button>
                </>
              ) : (
                <ImageIcon className="w-6 h-6 text-stone-300" />
              )}
            </div>
            <label className="cursor-pointer">
              <Button variant="outline" size="sm" asChild><span>Upload Logo</span></Button>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleLogoUpload}
                data-testid="logo-upload-input"
              />
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-1">Recommended: 200x50px transparent PNG</p>
        </div>

        <div>
          <Label>Favicon</Label>
          <div className="mt-1.5 flex items-center gap-3">
            <div className="relative w-12 h-12 border rounded-lg overflow-hidden bg-stone-50 flex items-center justify-center">
              {siteIdentity.favicon ? (
                <>
                  <img 
                    src={siteIdentity.favicon.startsWith('http') ? siteIdentity.favicon : `${BACKEND_URL}${siteIdentity.favicon}`} 
                    alt="Favicon" 
                    className="w-full h-full object-contain" 
                  />
                  <button 
                    onClick={() => setSiteIdentity(prev => ({ ...prev, favicon: '' }))} 
                    className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-bl text-xs flex items-center justify-center"
                  >
                    &times;
                  </button>
                </>
              ) : (
                <div className="w-6 h-6 bg-amber-500 rounded" />
              )}
            </div>
            <label className="cursor-pointer">
              <Button variant="outline" size="sm" asChild><span>Upload Favicon</span></Button>
              <input 
                type="file" 
                accept="image/png,image/x-icon,image/svg+xml" 
                className="hidden" 
                onChange={handleFaviconUpload}
                data-testid="favicon-upload-input"
              />
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-1">HandyBayani logo shown if no favicon set</p>
        </div>
      </div>

      {/* Social Media Sharing Image (OG Image) */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <Label className="text-slate-700 font-medium">Social Media Sharing Image</Label>
        <p className="text-xs text-slate-500 mb-3">
          This image appears when sharing your site URL on Facebook, Twitter, LinkedIn, etc.
        </p>
        <div className="flex items-start gap-4">
          <div className="relative w-40 h-24 border rounded-lg overflow-hidden bg-stone-50 flex items-center justify-center">
            {siteIdentity.og_image ? (
              <>
                <img 
                  src={siteIdentity.og_image.startsWith('http') ? siteIdentity.og_image : `${BACKEND_URL}${siteIdentity.og_image}`} 
                  alt="OG Image" 
                  className="w-full h-full object-cover" 
                />
                <button 
                  onClick={() => setSiteIdentity(prev => ({ ...prev, og_image: '' }))} 
                  className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white rounded-bl text-xs flex items-center justify-center"
                >
                  &times;
                </button>
              </>
            ) : (
              <div className="text-xs text-slate-400 text-center p-2">No OG image set</div>
            )}
          </div>
          <div className="flex-1">
            <label className="cursor-pointer">
              <Button variant="outline" size="sm" asChild><span>Upload OG Image</span></Button>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleOgImageUpload}
                data-testid="og-image-upload-input"
              />
            </label>
            <p className="text-xs text-slate-400 mt-1">Recommended size: 1200x630px</p>
          </div>
        </div>

        {/* OG Description */}
        <div className="mt-4">
          <Label className="text-slate-700">Social Media Description</Label>
          <Input
            value={siteIdentity.og_description || ''}
            onChange={(e) => setSiteIdentity(prev => ({ ...prev, og_description: e.target.value }))}
            placeholder="A brief description shown when sharing on social media"
            className="mt-1.5"
            data-testid="og-description-input"
          />
          <p className="text-xs text-slate-400 mt-1">Default: &quot;{siteIdentity.site_tagline}&quot;</p>
        </div>
      </div>

      <Button 
        onClick={onSave}
        disabled={isSaving}
        className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
        data-testid="save-site-identity-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Site Identity'}
      </Button>
    </div>
  );
}
